<template>
	<Dialog v-model:visible="visible" :closeable="true" class="custom-header custom-footer" style="width: 500px;" :contentStyle="{ padding: '0' }">
		<template #header>
			<div class="ml-3 flex flex-col">
				<div class="text-sm font-light mt-2 opacity-85">{{ queue.name }}</div>
				<div class="text-3xl opacity-85" style="margin-top:-5px">{{ t('queue.calls.header1') }} {{ types[callsType] }} {{ t('queue.calls.header2') }}</div>
			</div>
		</template>

		<div style="height:380px;overflow:scroll" v-if="calls">
			<div @click.prevent="showMenu($event, item)" class="call-list-item" v-for="(item,index) in calls" :key="item._id" :class="{'odd':index % 2 === 0}">
				<div style="display:flex;justify-content:center;flex-direction:column" class="dotdotdot">
					<div class="dotdotdot">{{ss.cutNationalPrefix(item.aNumber, true) }}{{ item.aNumberName ? ' • ' + item.aNumberName : '' }}</div>
					<div v-if="item.answeredByExtensionName" style="margin-top:-3px;display:flex;font-size:11px;color:var(--label-otherparty-color)">
						<GlyphIcon style="margin-top:-1px" :icon="GlyphIconName['call']" size="12" color="var(--label-otherparty-color)" />
						{{ item.answeredByExtensionName }}
						<span style="color:var(--label-expires-color);font-size:var(--font-size-small)">{{ item.status != 'ANSWERED' ? '&nbsp;(håndteret)' : '' }}</span>
					</div>
				</div>
				<div style="display:flex;justify-content:center;flex-direction:column;text-align:right">
					<div style="font-size:12px">{{ $t('common.at') }} {{ formatDate(item.entered, 'HH:mm') }}</div>
					<div style="font-size:12px;color:var(--label-otherparty-color);margin-top:-3px">{{ shortduration(duration(item), false) }} {{ shortduration(duration(item), true) }}</div>
				</div>
			</div>
			<div disabled v-if="calls.length==0" class="italic text-center mt-4">{{ $t('queue.calls.noCallsFound') }}</div>
		</div>

		<ContextMenu ref="contextMenu" :model="contextMenuItems" appendTo="body" popup />

		<template #footer>
			<Button severity="secondary" size="small" @click="close()" tabindex="110" :label="t('common.close')" class="w-36" />
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useEmployeesStore } from '@/stores/Employees'
import { useApiStore } from '@/stores/Api'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import SipClient from '@/utilities/SipClient'
import GlyphIcon from '@/components/GlyphIcon.vue'
import type { PbxAppQueuesAllItem } from '@/TcxApi/PbxApp'
import { ref } from 'vue'
import { type PbxQueueCallsResponse, type PbxQueueCallsCall } from '@/TcxApi/PbxQueue'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { formatDate, shortduration } from '@/utilities/filters'
import type { MenuItem } from 'primevue/menuitem'
import type ContextMenu from 'primevue/contextmenu'

const ss = useSettingsStore()
const employees = useEmployeesStore()
const api = useApiStore()
const sipClient = SipClient.instance
const { t } = useI18n()

interface Props {
	queue: PbxAppQueuesAllItem
}

const props = defineProps<Props>()
const types: Record<string, string> = {
	UNANSWERED: t('queue.calls.UNANSWERED'),
	ANSWERED: t('queue.calls.ANSWERED'),
	NOMEMBERS: t('queue.calls.NOMEMBERS'),
	ALLBUSY: t('queue.calls.ALLBUSY'),
	TIMEOUT: t('queue.calls.TIMEOUT'),
	CANCELLED: t('queue.calls.CANCELLED')
}
const calls = ref<PbxQueueCallsCall[]>([])
const callsType = ref('')
const currentMenu = ref<PbxQueueCallsCall | null>(null)
const visible = ref(false)
const contextMenu = ref<InstanceType<typeof ContextMenu>>()
const contextMenuItems = ref<MenuItem[]>([
	{ label: t('queue.call'), icon: 'pi pi-phone', command: call },
	{ label: t('queue.markAsHandled'), icon: 'pi pi-check', command: markAsHandled }
])

async function load() {
	if (!props.queue || !callsType.value) {
		calls.value = []
		return
	}

	let status = callsType.value
	if (status === 'UNANSWERED') { status = '!ANSWERED' }
	if (status === 'ALLBUSY') { status = 'ALL_BUSY' }
	if (status === 'NOMEMBERS') { status = 'NO_MEMBERS' }

	try {
		const res = await api.get<PbxQueueCallsResponse>(`/pbx/queue/${props.queue._id}/calls?reverse=true&limit=100&status=${status}`)

		for (const call of res.data.calls) {
			if (call.entered) { call.entered = new Date(call.entered) }
			if (call.answered) { call.answered = new Date(call.answered) }
			if (call.ended) { call.ended = new Date(call.ended) }
		}
		calls.value = res.data.calls
	}
	catch (_) { /** Ignore */ }
}

function duration(call: PbxQueueCallsCall) {
	if (call.entered && call.ended) {
		return Math.floor((call.ended.getTime() - call.entered.getTime()) / 1000)
	}
	return 0
}

function show(key: string) {
	callsType.value = key
	calls.value = []
	load()
	visible.value = true
}

function close() {
	calls.value = []
	callsType.value = ''
	visible.value = false
}

function showMenu(event: MouseEvent, item: PbxQueueCallsCall) {
	currentMenu.value = item
	contextMenu.value?.show(event)
}

async function call() {
	const item = currentMenu.value

	if (!item) { return }
	if (ss.settings.softphoneId) {
		const target = ss.cutNationalPrefix(item.aNumber)
		if (target) {
			sipClient.call(target)
			close()
		}
	} else {
		const target = ss.cutNationalPrefix(item.aNumber)
		if (target) {
			try { await api.get(`/pbx/app/action/call?device=${ss.realPhoneId}&target=${encodeURIComponent(target)}`) }
			catch (_) {
				globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') })
			}
			close()
		}
	}
}

async function markAsHandled() {
	const item = currentMenu.value

	if (!item || item.answeredByExtension || item.answeredByNumber) { return }

	const extension = employees.me?.primaryExtension
	if (extension) {
		try {
			await api.post(`/pbx/queue/${item.queue}/calls/${item._id}/handled`, { extension: extension._id })
			item.answeredByExtension = extension._id
			item.answeredByExtensionName = employees.me.name
		}
		catch (_) {
			globalEvents.emit('CommonErrorModal', { header: t('queue.markAsHandled'), message: t('queue.markAsHandledFailed') })
		}
	}
}

globalEvents.on('Auth:LoggedOut', close)
globalEvents.on('Realtime:Disconnected', close)

defineExpose({ show })
</script>

<style scoped>
	.call-list-item {
		display: grid;
		grid-template-columns: 1fr auto;
		text-align: left;
		border-bottom: 1px solid var(--p-form-field-border-color);
		padding-left: 10px;
		padding-right: 10px;
		cursor: pointer;
	}
	.call-list-item.odd {
		background-color: #00000010;
	}
	.call-list-item:hover {
		background-color: var(--p-primary-color);
		color: var(--p-primary-contrast-color);
	}
</style>
